import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';

export interface PurchaseDetails {
  id: string;
  sequence: {
    id: string;
    title: string;
    downloadUrl: string;
    artist?: string;
    price: number;
  };
  purchaseDate: string;
  amountPaid: number;
}

// Define the type for the response from the get_purchase_details RPC
interface PurchaseDetailsResponse {
  id: string;
  sequence_id: string;
  amount_paid: number;
  created_at: string;
}

interface DownloadToken {
  downloadUrl: string;
  token: string;
  expiresAt: string;
  attemptsAllowed: number;
}

export const usePurchaseDetails = (id: string | undefined) => {
  const { user } = useAuth();
  const [purchase, setPurchase] = useState<PurchaseDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadStore = useDownloadFlowStore();
  
  useEffect(() => {
    const fetchPurchaseDetails = async () => {
      if (!user || !id) return;
      
      setIsLoading(true);
      try {
        // Use RPC call to get purchase details with correct typing
        const { data, error } = await supabase.rpc<PurchaseDetailsResponse[]>('get_purchase_details', {
          p_user_id: user.id,
          p_sequence_id: id
        });
          
        if (error) throw error;
        
        // Check if data exists and has items
        if (data && Array.isArray(data) && data.length > 0) {
          const purchaseItem = data[0];
          
          // Get sequence details for more complete information
          const { data: sequence, error: sequenceError } = await supabase
            .from('sequences')
            .select('title, price, song:songs(artist)')
            .eq('id', purchaseItem.sequence_id)
            .single();

          if (sequenceError) {
            console.warn('Could not fetch sequence details:', sequenceError);
          }

          setPurchase({
            id: purchaseItem.id,
            sequence: {
              id: purchaseItem.sequence_id,
              title: sequence?.title || "Holiday Light Sequence",
              downloadUrl: `/api/cdn/secure-download/${purchaseItem.sequence_id}`,
              artist: sequence?.song?.artist || 'Unknown Artist',
              price: sequence?.price || 0,
            },
            purchaseDate: new Date(purchaseItem.created_at).toLocaleDateString(),
            amountPaid: purchaseItem.amount_paid
          });
        }
      } catch (error) {
        console.error("Error fetching purchase details:", error);
        toast.error("Failed to load purchase details", {
          description: "Please try again or contact support."
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPurchaseDetails();
  }, [id, user]);
  
  const handleDownload = async () => {
    if (!purchase || !user) return;
    
    setIsDownloading(true);
    downloadStore.setGeneratingUrl(true);
    downloadStore.setDownloadError(null);

    try {
      // Start download session in store
      downloadStore.startDownloadSession({
        sequenceId: purchase.sequence.id,
        sequenceTitle: purchase.sequence.title,
        sequenceArtist: purchase.sequence.artist || 'Unknown Artist',
        isOwner: false, // This is a purchased sequence
        isFree: purchase.amountPaid === 0,
        price: purchase.sequence.price,
        purchaseId: purchase.id,
        userId: user.id,
      });

      // Get current auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      downloadStore.setDownloading(true, 25);

      // Generate secure download URL
      const response = await fetch('/api/cdn/download-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          sequenceId: purchase.sequence.id,
          userId: user.id,
          purchaseId: purchase.id,
          expirationHours: 48, // 48 hours for purchased content re-downloads
          maxAttempts: 5,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate download URL');
      }

      const tokenData: DownloadToken = await response.json();
      downloadStore.setDownloadUrl(tokenData.downloadUrl, tokenData.token);
      downloadStore.setDownloading(true, 75);

      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = tokenData.downloadUrl;
      link.download = `${purchase.sequence.title.replace(/[^a-zA-Z0-9\s]/g, '_')}.zip`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      downloadStore.setDownloading(true, 100);
      
      toast.success("Re-download started", {
        description: "Your purchased sequence is being downloaded."
      });
      
      // Complete download progress after a short delay
      setTimeout(() => {
        downloadStore.resetDownloadProgress();
        
        // Show appropriate modals based on sequence type and user preferences
        if (purchase.amountPaid === 0) {
          // Free sequence - check if should show donation modal
          if (downloadStore.shouldShowDonationModal()) {
            downloadStore.showDonationModal();
          } else if (downloadStore.shouldShowAddedToDisplayModal()) {
            downloadStore.showAddedToDisplayModal();
          }
        } else {
          // Paid sequence - show added to display modal if appropriate
          if (downloadStore.shouldShowAddedToDisplayModal()) {
            downloadStore.showAddedToDisplayModal();
          }
        }
      }, 1000);

    } catch (error) {
      console.error("Error downloading sequence:", error);
      downloadStore.setDownloadError(error instanceof Error ? error.message : 'Download failed');
      toast.error("Download failed", {
        description: error instanceof Error ? error.message : "There was a problem starting your download. Please try again."
      });
    } finally {
      setIsDownloading(false);
      downloadStore.setGeneratingUrl(false);
    }
  };
  
  return {
    purchase,
    isLoading,
    isDownloading,
    handleDownload
  };
};
